import React, { useEffect, useState } from 'react'
import { Button, Col, Form, InputGroup, Modal, Row, Spinner } from 'react-bootstrap'
import { Autocomplete } from '@react-google-maps/api'
import { useTranslation } from 'react-i18next'
import { observer } from 'mobx-react-lite'
import { currencyValue, tags } from '../components/muiStyle/muiStyle'
import PhotoComponent from '../components/post/PhotoComponent'
import TagsComponent from '../components/post/TagsComponent'
import ErrorMessage from '../components/ErrorMessage'
import { useStore } from '../../app/stores/store'

function PostCreate({ handleClosePost }: any) {
  const initialValue = {
    id: '',
    title: '',
    description: '',
    category: 'house',
    price: 0,
    address: '',
    city: '',
    slug: '',
    rooms: 0,
    meters: 0,
    zipCode: 0,
    type: 2,
    tags: [],
    country: 0,
    currency: 0,
    postImages: [],
  }
  const { postListStore, postStore } = useStore()
  const { createPosts } = postStore
  const [wizard, setWizard] = useState(0)
  const [files, setFiles] = useState<any>([])
  const [image, setImage] = useState<any>([])
  const [post, setPost] = useState(initialValue)
  const [value, setValue] = useState<any>()
  const [error, setError] = useState<any>('')
  const [loadingBtn, setLoadingBtn] = useState(false)
  const { t } = useTranslation()
  const [selectedCountry, setSelectedCountry] = useState('')

  useEffect(() => {
    if (post.country === 0) {
      setSelectedCountry('CH')
    } else if (post.country === 1) {
      setSelectedCountry('PL')
    }
  }, [post.country])

  const onLoadPlaces = (e: any) => {
    setValue(e)
  }
  const onPlacesChanged = () => {
    const google = value.getPlace()
    const components = google.address_components
    const data = { ...post }
    let streetNumber = ''

    components.map((c: any) => {
      if (c.types.includes('route')) data.address = c.long_name
      if (c.types.includes('locality')) data.city = c.long_name
      if (c.types.includes('postal_code')) data.zipCode = c.long_name
      if (c.types.includes('street_number')) streetNumber = c.long_name
      return null
    })

    data.address = streetNumber
      ? (data.address = `${data.address} ${streetNumber}`)
      : (data.address = `${data.address}`)

    setPost(data)
  }

  const getCountryCode = (countryValue: number): string => {
    const countryCodeMap: Record<number, string> = {
      0: 'CH',
      1: 'PL',
    }

    return countryCodeMap[countryValue] || ''
  }

  const handleInputChange = (e: React.ChangeEvent<any>) => {
    const { name, value } = e.target

    if (name === 'country') {
      setSelectedCountry(getCountryCode(parseInt(value)))

      if (parseInt(value) === 0 || parseInt(value) === 1) {
        setPost({
          ...post,
          [name]: value,
          city: '',
          address: '',
          zipCode: 0,
        })
      } else {
        setPost({
          ...post,
          [name]: value,
        })
      }
    } else if (name === 'type' && value === '2') {
      setPost({ ...post, [name]: parseInt(value), price: 0 })
    } else if (name === 'price') {
      const enteredValue = parseFloat(value)
      const priceInCents = Math.round(enteredValue * 100)
      setPost({ ...post, [name]: priceInCents })
    } else {
      setPost({ ...post, [name]: value })
    }
  }
  const displayedPrice = (post.price / 100).toFixed(2).replace(/\.00$/, '')

  const goToNextStep = () => {
    if (!post.title) {
      setError(t('err_title'))
    } else if (!post.description) {
      setError(t('err_desc'))
    } else if (!post.address) {
      setError(t('err_address'))
    } else if (!post.city) {
      setError(t('err_city'))
    } else if (post.zipCode <= 0) {
      setError(t('err_zipcode'))
    } else if (!post.category) {
      setError(t('err_category'))
    } else {
      setError('')
      setWizard(wizard + 1)
    }
  }

  const goBack = () => {
    setWizard(wizard - 1)
  }

  const handleSubmit = (e: any) => {
    setLoadingBtn(true)
    e.preventDefault()
    let primaryFileExists = false

    for (let i = 0; i < files.length; i++) {
      if (files[i].primary) {
        primaryFileExists = true
        break
      }
    }
    if (!post.category) {
      setLoadingBtn(false)
      setError(t('err_category'))
    } else if (!post.price && !(post.type.toString() === '2')) {
      setLoadingBtn(false)
      setError(t('err_price'))
    } else if (post.price > 10000000000) {
      setLoadingBtn(false)
      setError(t('price_too_big'))
    } else if (!post.rooms) {
      setLoadingBtn(false)
      setError(t('err_rooms'))
    } else if (!post.meters) {
      setLoadingBtn(false)
      setError(t('err_metes'))
    } else if (post.tags.length === 0) {
      setLoadingBtn(false)
      setError(t('err_tags'))
    } else if (files.length === 0) {
      setLoadingBtn(false)
      setError(t('photo_err1'))
    } else if (files.length > 15) {
      setLoadingBtn(false)
      setError(t('photo_err2'))
    } else if (!primaryFileExists) {
      setLoadingBtn(false)
      setError(t('photo_err3'))
    } else {
      setError('')
      createPosts(post, files, image)
        .then(() => {
          postListStore.loadPosts()
        })
        .finally(() => {
          setLoadingBtn(false)
          handleClosePost()
        })
    }
  }

  const handleDelete = (value: string) => {
    setPost((tag) => ({
      ...tag,
      tags: tag.tags.filter((name) => name !== value),
    }))
  }

  const handlePrimary = (e: any, index: any) => {
    const newArray = files.map((img: any, i: any) => ({
      ...img,
      primary: i === index,
    }))
    setFiles(newArray)
  }

  const handleRemoveImage = (index: number) => {
    const newFiles = [...files]
    newFiles.splice(index, 1)
    setFiles(newFiles)

    const newImages = [...image]
    newImages.splice(index, 1)
    setImage(newImages)

    if (newFiles.length > 0 && !newFiles.some((file) => file.primary)) {
      newFiles[0].primary = true
      setFiles(newFiles)
    }
  }

  const handleBlurEvent = (event: any) => {
    const address = event.target.value.split(',').slice(0, 1)
    event.target.value = address
  }

  const countryOptions = [
    { value: 0, label: 'Switzerland' },
    { value: 1, label: 'Poland' },
  ]

  const formData = [
    {
      md: 12,
      className: 'card-input-style form-control-spacing',
      placeholder: t('enter_title'),
      type: 'text',
      name: 'title',
      value: post.title,
    },
    {
      md: 12,
      className: 'card-input-style form-control-spacing',
      placeholder: t('enter_desc'),
      type: 'text',
      name: 'description',
      value: post.description,
    },
    {
      md: 6,

      component: (
        <select
          className='card-input-style form-control-spacing select-width'
          name='country'
          value={post.country}
          onChange={handleInputChange}
        >
          {countryOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ),
    },
    {
      md: 6,
      component: (
        <Autocomplete
          onLoad={onLoadPlaces}
          restrictions={{ country: selectedCountry }}
          types={['address']}
          className='autocomplete-input'
          onPlaceChanged={onPlacesChanged}
        >
          <input
            type='text'
            name='address'
            className='form-control card-input-style form-control-spacing'
            placeholder={t('address')!}
            value={post.address}
            required
            onBlur={handleBlurEvent}
            onChange={handleInputChange}
            min='1'
          />
        </Autocomplete>
      ),
    },
    {
      md: 6,
      className: 'card-input-style form-control-spacing',
      placeholder: t('city'),
      type: 'text',
      name: 'city',
      value: post.city,
    },
    {
      md: 6,
      className: 'card-input-style form-control-spacing',
      placeholder: t('zipcode'),
      type: 'text',
      name: 'zipCode',
      value: post.zipCode > 0 ? post.zipCode : '',
    },
  ]

  const formData2 = [
    {
      md: 4,
      sm: 8,
      label: t('price'),
      classLabel: 'login-p',
      component: (
        <InputGroup>
          <Form.Control
            className='price-input-style'
            placeholder='1 - 1 000 000'
            type='number'
            name='price'
            value={post.type.toString() === '2' ? 0 : post.price > 0 ? displayedPrice : ''}
            disabled={post.type.toString() === '2'}
            onChange={handleInputChange}
          />
          <InputGroup.Text className='double-zero'>.00</InputGroup.Text>
        </InputGroup>
      ),
    },
    {
      md: 2,
      sm: 4,
      label: 'Currency',
      classLabel: 'login-p hide-label',
      component: (
        <div style={{ height: '60px' }}>
          <select
            className='select-input-style text-center form-control-spacing select-width'
            name='currency'
            onChange={handleInputChange}
            value={post.currency}
          >
            {currencyValue.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      ),
    },
    {
      md: 3,
      sm: 6,
      label: t('room'),
      classLabel: 'login-p',
      placeholder: '1 - 25',
      type: 'number',
      name: 'rooms',
      className: 'card-input-style',
      value: post.rooms > 0 ? post.rooms : '',
    },
    {
      md: 3,
      sm: 6,
      label: 'Meters²',
      classLabel: 'login-p',
      placeholder: '1 - 500',
      type: 'number',
      name: 'meters',
      className: 'card-input-style mb-3',
      value: post.meters > 0 ? post.meters : '',
    },

    {
      md: 12,
      label: t('tags'),
      classLabel: 'login-p',
      component: (
        <TagsComponent
          post={post}
          handleInputChange={handleInputChange}
          handleDelete={handleDelete}
          tags={tags}
        />
      ),
    },
    {
      md: 12,
      label: t('images'),
      classLabel: 'login-p pt-4',
      component: (
        <PhotoComponent
          postImages={post.postImages!}
          files={files}
          setFiles={setFiles}
          setImage={setImage}
          handlePrimary={handlePrimary}
          handleRemoveImage={handleRemoveImage}
        />
      ),
    },
  ]

  const formCheck = [
    { label: t('house'), name: 'category', value: 'house' },
    { label: t('office'), name: 'category', value: 'office' },
    { label: t('appartment'), name: 'category', value: 'appartment' },
    { label: t('villa'), name: 'category', value: 'villa' },
    { label: t('buy_now'), name: 'type', value: 0 },
    // { label: t('first_wins'), name: 'type', value: 1 }, // temporarily hidden
    { label: t('auction'), name: 'type', value: 2 },
  ]

  const buttons = [
    {
      title: t('cancel'),
      onClick: handleClosePost,
      className: 'text-white payment-button-cancel',
      wizard: 0,
    },
    {
      title: t('next'),
      onClick: goToNextStep,
      className: 'text-white payment-button-tryagain',
      wizard: 0,
    },
    {
      title: t('previous'),
      onClick: goBack,
      className: 'text-white payment-button-cancel',
      wizard: 1,
    },
    {
      title: loadingBtn ? <Spinner animation='border' variant='light' /> : t('create'),
      onClick: handleSubmit,
      className: 'text-white payment-button-tryagain',
      wizard: 1,
      type: 'submit',
    },
  ]

  return (
    <div className='newFont prevent-select'>
      <Modal.Header className='bg-white'>
        <p className='post-card-title'>{t('create_post')}</p>
        <Button variant='inherit' className='close me-2' onClick={handleClosePost}>
          <i className='fa-regular fa-x icon-size' />
        </Button>
      </Modal.Header>
      <Modal.Body className='post-card-padding card-font-size'>
        <Form onSubmit={handleSubmit}>
          {wizard === 0 && (
            <div className='pt-3'>
              <Row className='pb-4'>
                {formData.map((field, index) => (
                  <Col key={index} md={field.md}>
                    {field.component ? (
                      field.component
                    ) : (
                      <Form.Control
                        className={field.className}
                        placeholder={field.placeholder}
                        type={field.type}
                        onChange={handleInputChange}
                        name={field.name}
                        value={field.value}
                      />
                    )}
                  </Col>
                ))}
              </Row>
              <hr className='auction-create-hr' />
              <div className='pt-3 card-font-size'>
                <p className='text-black fw-bold'>{t('select_category')}</p>
                <Form.Group className='d-flex align-items-center gap-content text-black pt-2 center-content mb-2 flex-wrap'>
                  {formCheck.slice(0, 4).map((check, index) => (
                    <label key={index} className='d-flex gap-2 text-center'>
                      <Form.Check
                        type='radio'
                        className='text-black mb-0'
                        name={check.name}
                        value={check.value}
                        onChange={handleInputChange}
                        defaultChecked={post.category === check.value}
                      />
                      {check.label}
                    </label>
                  ))}
                </Form.Group>
              </div>
            </div>
          )}
          {wizard === 1 && (
            <div>
              <div className='card-font-size'>
                <p className='text-black fw-bold'>{t('select_category')}</p>
                <Form.Group className='d-flex align-items-center gap-content text-black center-content mb-2'>
                  {formCheck.slice(4).map((check, index) => (
                    <label key={index} className='d-flex gap-2 text-center'>
                      <Form.Check
                        type='radio'
                        className='text-black mb-0'
                        name={check.name}
                        value={check.value}
                        onChange={handleInputChange}
                        defaultChecked={post.type === check.value}
                      />
                      {check.label}
                    </label>
                  ))}
                </Form.Group>
              </div>
              <Row className='pt-3 center-input'>
                {formData2.map((field, index) => (
                  <Col key={index} md={field.md} sm={field.sm}>
                    <Form.Label className={field.classLabel}>{field.label}</Form.Label>
                    {field.component ? (
                      field.component
                    ) : (
                      <Form.Control
                        className={field.className}
                        placeholder={field.placeholder}
                        type={field.type}
                        name={field.name}
                        value={field.value}
                        // disabled={field.disabled}
                        onChange={handleInputChange}
                      />
                    )}
                  </Col>
                ))}
              </Row>
            </div>
          )}
          {error && <ErrorMessage text={error} setError={() => setError('')} />}
          <div className='auction-create-buttons'>
            {buttons.map(
              (button, index) =>
                wizard === button.wizard && (
                  <button
                    key={index}
                    onClick={button.onClick}
                    className={`text-white ${button.className}`}
                    type={button.type === 'submit' ? 'submit' : 'button'}
                    disabled={loadingBtn}
                  >
                    {button.title}
                  </button>
                ),
            )}
            
            {/* <InformationModal
              text={wizard === 0 ? t('post_create.description_1') : t('post_create.description_2')}
            /> */}
          </div>
        </Form>
      </Modal.Body>
    </div>
  )
}
export default observer(PostCreate)
